import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Radio } from "lucide-react";
import { fetchArtists, fetchRankingsMeta } from "@/lib/api";
import { RankBadge } from "@/components/RankBadge";
import { AdSlot } from "@/components/AdSlot";
import { Seo } from "@/components/Seo";
import { ShareButtons } from "@/components/ShareButtons";

const PILLAR_SORTS = [
  { key: "overall", label: "Overall" },
  { key: "charts", label: "Charts" },
  { key: "press", label: "Press Buzz" },
  { key: "fans", label: "Fanbase" },
];

export const REGION_PAGES = {
  us: { key: "us", title: "US Power Index", short: "US", blurb: "The artists running American culture right now — ranked from the US Apple Music charts, press buzz and fanbase." },
  uk: { key: "gb", title: "UK Power Index", short: "UK", blurb: "Britain's chart leaders — from UK drill and grime to pop crossovers — ranked monthly from the UK charts, press and fanbase." },
  europe: { key: "eu", title: "Europe Power Index", short: "Europe", blurb: "Who's moving the continent: artists charting across France, Germany, Italy, Spain and the Netherlands." },
  kpop: { key: "kr", title: "K-Pop Power Index", short: "K-Pop", blurb: "South Korea's chart kings and queens — the idols, groups and soloists dominating the Korean charts this month." },
  canada: { key: "ca", title: "Canada Power Index", short: "Canada", blurb: "From Toronto to Vancouver — the artists leading the Canadian charts and conversation this month." },
  afrobeats: { key: "africa", title: "Afrobeats Power Index", short: "Afrobeats", blurb: "Lagos to Johannesburg — the Afrobeats and amapiano stars charting across Nigeria and South Africa right now." },
};

export default function Rankings() {
  const { region } = useParams();
  const page = region ? REGION_PAGES[region] : null;
  const [sortBy, setSortBy] = useState("overall");
  const [artists, setArtists] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    fetchRankingsMeta().then(setMeta).catch(() => {});
  }, []);
  useEffect(() => {
    setSortBy("overall");
  }, [region]);
  useEffect(() => {
    const key = page ? page.key : sortBy;
    fetchArtists(key).then((list) => {
      if (page) {
        const regional = list.filter((a) => (a.regions?.[page.key] ?? 0) > 0).map((a, i) => ({ ...a, rank: i + 1 }));
        setArtists(regional);
      } else setArtists(list);
    }).catch(() => {});
  }, [sortBy, page]);

  const regions = meta?.regions || [];
  const scoreFor = (a) => {
    if (page) return a.regions?.[page.key] ?? 0;
    if (sortBy === "overall") return a.overall;
    return a.scores?.[sortBy] ?? 0;
  };
  const updated = meta?.last_success ? new Date(meta.last_success).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
  const tabCls = (active) =>
    `font-mono text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
      active ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/70 hover:border-signal hover:text-signal"
    }`;

  if (region && !page) {
    return (
      <div className="text-center py-32">
        <Seo title="Region not found" path={`/rankings/${region}`} noindex />
        <h1 className="font-anton text-5xl uppercase">404</h1>
        <Link to="/rankings" className="inline-block mt-6 font-mono text-xs uppercase tracking-widest text-signal">← Back to rankings</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
      <Seo
        title={page ? page.title : "Power Index Rankings"}
        description={page ? page.blurb : "The auto-ranked global leaderboard of artists running culture — scored from Apple Music charts across the US, UK, Europe, K-Pop, Canada and Afrobeats, plus press buzz and fanbase."}
        path={page ? `/rankings/${region}` : "/rankings"}
      />
      <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">
        {page ? <Link to="/rankings" className="hover:underline">The Power Index</Link> : "The Power Index"}
        {page && ` / ${page.short}`}
      </span>
      <h1 className="font-anton text-6xl md:text-8xl uppercase tracking-tight mt-3 leading-none" data-testid="rankings-title">
        {page ? page.title : "Rankings"}
      </h1>
      <p className="text-muted-foreground text-lg mt-4 max-w-2xl">
        {page ? page.blurb : "The global leaderboard of artists running culture — ranked automatically from the charts in the US, UK, Europe, South Korea, Canada and Africa, plus press buzz and fanbase."}
      </p>
      {updated && (
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mt-3 inline-flex items-center gap-2" data-testid="rankings-updated">
          <Radio className="w-3.5 h-3.5 text-signal" /> Live data · updated {updated}
        </p>
      )}
      {page && <ShareButtons title={`${page.title} — NERO SUN`} className="mt-5" />}

      <AdSlot variant="leaderboard" slot={process.env.REACT_APP_ADSENSE_SLOT_RANKINGS} className="mt-8" />

      {!page && (
        <div className="flex flex-wrap gap-2 mt-10">
          {PILLAR_SORTS.map((s) => (
            <button key={s.key} data-testid={`sort-${s.key}`} onClick={() => setSortBy(s.key)} className={tabCls(sortBy === s.key)}>
              {s.label}
            </button>
          ))}
        </div>
      )}
      {regions.length > 0 && (
        <div className={`flex flex-wrap items-center gap-2 ${page ? "mt-10" : "mt-3"}`}>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Regional charts</span>
          {regions.map((r) => (
            <Link key={r.key} to={`/rankings/${r.slug}`} data-testid={`region-link-${r.slug}`} className={tabCls(page?.key === r.key)}>
              {r.label}
            </Link>
          ))}
          {page && (
            <Link to="/rankings" data-testid="region-link-global" className={tabCls(false)}>Global</Link>
          )}
        </div>
      )}

      <div className="hidden md:grid grid-cols-[60px_1fr_120px_100px] gap-4 px-6 mt-10 pb-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        <span>Rank</span>
        <span>Artist</span>
        <span className="text-center">Move</span>
        <span className="text-right">{page ? `${page.short} score` : "Score"}</span>
      </div>

      <div className="border-x border-b border-white/10 divide-y divide-white/10 md:border-x md:border-b">
        {artists.length === 0 && (
          <p className="font-mono text-sm text-muted-foreground p-6" data-testid="rankings-empty">
            {page ? `No artists in the index are charting in ${page.short} this month.` : "The index is being compiled from this month's charts. Check back shortly."}
          </p>
        )}
        {artists.map((a) => (
          <Link
            key={a.slug}
            to={`/artist/${a.slug}`}
            data-testid={`ranking-row-${a.slug}`}
            className="group grid grid-cols-[44px_1fr_auto] md:grid-cols-[60px_1fr_120px_100px] gap-4 items-center p-4 md:px-6 hover:bg-secondary transition-colors"
          >
            <span className="font-mono-stat text-2xl md:text-4xl text-signal tabular-nums">
              {String(a.rank).padStart(2, "0")}
            </span>
            <div className="flex items-center gap-4 min-w-0">
              <img src={a.image} alt={a.name} className="w-12 h-12 md:w-16 md:h-16 object-cover grayscale group-hover:grayscale-0 transition-all" />
              <div className="min-w-0">
                <div className="font-display text-lg md:text-2xl font-medium group-hover:text-signal transition-colors truncate">
                  {a.name}
                </div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground truncate">
                  {a.top_song ? `"${a.top_song}" · ` : ""}{a.city}
                </div>
              </div>
            </div>
            <div className="md:flex md:justify-center">
              <RankBadge movement={a.movement} />
            </div>
            <span className="font-mono-stat text-2xl md:text-3xl tabular-nums text-right col-start-3 md:col-auto row-start-1 md:row-auto">
              {scoreFor(a)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
